import createMailFolderTableQuery from 'mini-mail-list-store/lib/utils/tables/createMailFolderTableQuery';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getSelectedFolderId from 'owa-mail-store/lib/utils/getSelectedFolderId';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import getTableViewFromTableQuery from 'mini-mail-list-store/lib/actions/tables/getTableViewFromTableQuery';
import isFocusedInboxEnabled from 'mini-mail-focused-inbox-config/lib/isFocusedInboxEnabled';
import { action } from 'satcheljs/lib/legacy';
import { getFolderName } from 'mini-mail-list-store/lib/selectors/getFolderName';
import { INBOX_FOLDER_NAME } from 'mini-mail-common/lib/utils/folderNameConstants';
import { isSuggestedUnsubscribeEnabled } from 'mini-mail-suggested-unsubscribe-store/lib/utils/isSuggestedUnsubscribeEnabled';
import { lazyCreateSuggestedUnsubscribeItems } from 'mini-mail-suggested-unsubscribe-store';
import { loadTableViewFromTableQuery } from 'mini-mail-list-actions';
import { safePerformanceMarkStart } from 'mini-performance';
import { setSelectedFolderId } from 'owa-mail-store/lib/actions/setSelectedFolderId';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import type { LoadMailOptions } from 'mini-mail-list-actions/lib/schema/LoadMailOptions';
import {
    FOLDER_CHANGED,
    FOLDER_MAILS_LOADED,
} from 'mini-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import type MailFolderTableQuery from 'mini-mail-list-store/lib/store/schema/MailFolderTableQuery';

export default action('selectFolder')(function selectFolder(
    folderId: string,
    focusedViewFilter: FocusedViewFilter = -1
): Promise<void> {
    const previousFolderId = getSelectedFolderId();
    const previousFocusedViewFilter =
        (getSelectedTableView()?.tableQuery as MailFolderTableQuery)?.focusedViewFilter ?? -1;
    if (folderId === previousFolderId && focusedViewFilter === previousFocusedViewFilter) {
        // Do not perform the rest of selecting folder logic when
        // user navigates to the current selected folder
        signalWorkflowEvent(FOLDER_MAILS_LOADED);
        return Promise.resolve();
    } else {
        signalWorkflowEvent(FOLDER_CHANGED);
        return selectFolderInternal(folderId, focusedViewFilter);
    }
});

export async function selectFolderInternal(
    folderNameOrId: string,
    focusedViewFilter: FocusedViewFilter = -1,
    options?: LoadMailOptions
): Promise<void> {
    safePerformanceMarkStart('SelectFolderNodeToMailListContentRender');
    // FolderId can be the real id of the folder or the distinguished name of it, like "inbox", "deletedItems", etc.
    const effectiveFolderId = folderNameToId(folderNameOrId) || folderNameOrId;
    let tableQuery = createMailFolderTableQuery(effectiveFolderId, focusedViewFilter);

    setSelectedFolderId(effectiveFolderId);

    const loadedTableView = await loadTableViewFromTableQuery(tableQuery, null, null, options);

    if (
        isSuggestedUnsubscribeEnabled() &&
        getFolderName(effectiveFolderId) === INBOX_FOLDER_NAME &&
        (!isFocusedInboxEnabled() || focusedViewFilter === 1)
    ) {
        lazyCreateSuggestedUnsubscribeItems.importAndExecute(
            getTableViewFromTableQuery(tableQuery)
        );
    }

    return loadedTableView;
}
